/**
 * Deadlines for the boot sequence.
 *
 * Everything on the loading path already handles a request that FAILS: the
 * asset loops swallow a 404 and carry on, and a rejected init throws the
 * player onto the error popup. Nothing handled a request that is ACCEPTED and
 * then never answered — a phone moving between cells, a captive portal, a
 * proxy holding the socket open. Those promises simply never settle, so the
 * boot stops dead with no error and no way out: the loader sits there for
 * good, or the intro popup does, with its wordmark looping forever.
 *
 * These two helpers put an end to that. Both are deliberately plain — no
 * retries, no backoff — because the caller already knows what a failure means
 * on its own path.
 */

/**
 * Reject if `promise` has not settled within `ms`.
 *
 * For a single request whose duration is known to be short (one JSON call).
 * The timer is always cleared, so a slow-but-successful call leaves nothing
 * behind.
 *
 * @param {Promise} promise
 * @param {number} ms
 * @param {string} label - named in the error, so a boot failure is readable
 *   in the console rather than an anonymous timeout.
 */
export const withTimeout = async (promise, ms, label) => {
  let timer = null;
  try {
    return await Promise.race([
      promise,
      new Promise((_, reject) => {
        timer = setTimeout(
          () => reject(new Error(`${label} timed out after ${ms}ms`)),
          ms,
        );
      }),
    ]);
  } finally {
    if (timer) clearTimeout(timer);
  }
};

/**
 * A deadline that resets every time something arrives.
 *
 * A flat timeout is the wrong tool for a whole asset bundle: it has to be set
 * long enough for the slowest honest connection, which makes it useless
 * against a stall, or short enough to catch a stall, which cuts off players
 * who were downloading perfectly well. What actually distinguishes the two is
 * PROGRESS — a slow connection keeps delivering files, a dead one delivers
 * nothing.
 *
 * So the guard gives up only after `stallMs` in which NOT ONE asset finished.
 * A player on 2G can take as long as they need; a socket that has gone quiet
 * is abandoned quickly.
 *
 * Usage — race the work against `guard.promise`, and call `guard.progress()`
 * as each piece lands:
 *
 *   const guard = createStallGuard(20000, "asset bundle");
 *   const work = items.map(async (i) => { try { await load(i); } finally { guard.progress(); } });
 *   try {
 *     await Promise.race([Promise.all(work), guard.promise]);
 *   } finally {
 *     guard.stop();
 *   }
 *
 * `guard.stop()` is required: without it the last armed timer keeps the boot
 * alive in the event loop and can reject after the work is already done.
 *
 * @param {number} stallMs - silence allowed before giving up
 * @param {string} label
 */
export const createStallGuard = (stallMs, label) => {
  let timer = null;
  let rejectGuard = null;
  let stopped = false;

  // Nothing ever resolves this — it exists only to lose the race, or to be
  // abandoned when the real work wins it.
  const promise = new Promise((_, reject) => {
    rejectGuard = reject;
  });
  // The guard is racing, so its rejection is always handled by the caller.
  // This keeps an unhandled-rejection warning off the console in the window
  // between stop() and the race unwinding.
  promise.catch(() => {});

  const arm = () => {
    if (stopped) return;
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => {
      if (stopped) return;
      rejectGuard(
        new Error(`${label} stalled — nothing arrived for ${stallMs}ms`),
      );
    }, stallMs);
  };

  return {
    promise,
    /** Start the clock. Call once, before the work begins. */
    start: arm,
    /** Something landed — reset the clock. Safe to call after stop(). */
    progress: arm,
    /** The work is over, one way or the other. Always call this. */
    stop: () => {
      stopped = true;
      if (timer) clearTimeout(timer);
      timer = null;
    },
  };
};
